import React, { useState, useEffect, useRef } from 'react';
import {
  Car, ChevronLeft, ChevronRight, Building, RefreshCw,
  Plane, Grid, ArrowLeft, ArrowRight, Home, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { format, addDays, subDays, isToday, getDaysInMonth, startOfMonth, getDay } from 'date-fns';
import DailyPlanning1Day from './DailyPlanning1Day';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

// View modes
const VIEW_MODES = {
  TWO_WEEK: 'two_week',
  SINGLE_DAY: 'single_day'
};

// Main Component - 2 Week Overview with Day View
const DailyPlanning = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // View state
  const [viewMode, setViewMode] = useState(VIEW_MODES.TWO_WEEK);

  // Calendar dropdown
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const calendarRef = useRef(null);

  // Day summaries for 2-week overview
  const [daySummaries, setDaySummaries] = useState({});

  // Headers for API calls
  const getHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    'Content-Type': 'application/json'
  });

  // Load projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const headers = getHeaders();
        const res = await axios.get(`${API_BASE_URL}/api/projects/`, { headers });
        setProjects(res.data || []);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  // Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Use selectedDate directly as start of 14-day period
  const periodStartDateStr = format(selectedDate, 'yyyy-MM-dd'); // String for useEffect dependency

  // Load day summaries - fetch for each day separately (like daily view does)
  useEffect(() => {
    if (!selectedProject || viewMode !== VIEW_MODES.TWO_WEEK) {
      return;
    }

    const fetchSummaries = async () => {
      try {
        const headers = getHeaders();
        const summaries = {};

        // Fetch data for each of the 21 days
        // We'll batch requests but call per-day for accuracy
        const dayPromises = [];
        for (let i = 0; i < 21; i++) {
          const date = addDays(new Date(periodStartDateStr), i);
          const dateStr = format(date, 'yyyy-MM-dd');

          dayPromises.push(
            Promise.all([
              axios.get(`${API_BASE_URL}/api/local-transports/pending_airport_pickups/`, {
                headers,
                params: { project: selectedProject, start_date: dateStr, end_date: dateStr }
              }).catch(() => ({ data: [] })),
              axios.get(`${API_BASE_URL}/api/local-transports/pending_airport_dropoffs/`, {
                headers,
                params: { project: selectedProject, start_date: dateStr, end_date: dateStr }
              }).catch(() => ({ data: [] })),
              axios.get(`${API_BASE_URL}/api/local-transports/pending_daily_to_project/`, {
                headers,
                params: { project: selectedProject, start_date: dateStr, end_date: dateStr }
              }).catch(() => ({ data: [] })),
              axios.get(`${API_BASE_URL}/api/local-transports/pending_daily_to_accommodation/`, {
                headers,
                params: { project: selectedProject, start_date: dateStr, end_date: dateStr }
              }).catch(() => ({ data: [] })),
              // Fetch scheduled transports for this day
              axios.get(`${API_BASE_URL}/api/local-transports/`, {
                headers,
                params: { project: selectedProject, start_date: dateStr, end_date: dateStr }
              }).catch(() => ({ data: [] }))
            ]).then(([pickupsRes, dropoffsRes, toProjectRes, fromProjectRes, scheduledRes]) => ({
              dateStr,
              pendingPickups: pickupsRes.data.length,
              pendingDropoffs: dropoffsRes.data.length,
              pendingToProject: toProjectRes.data.length,
              pendingFromProject: fromProjectRes.data.length,
              scheduledTransports: scheduledRes.data.length
            }))
          );
        }

        const dayResults = await Promise.all(dayPromises);

        dayResults.forEach(result => {
          const totalPending = result.pendingPickups + result.pendingDropoffs +
                               result.pendingToProject + result.pendingFromProject;
          summaries[result.dateStr] = {
            pendingPickups: result.pendingPickups,
            pendingDropoffs: result.pendingDropoffs,
            pendingToProject: result.pendingToProject,
            pendingFromProject: result.pendingFromProject,
            scheduledTransports: result.scheduledTransports,
            totalPending,
            hasWork: totalPending > 0 || result.scheduledTransports > 0
          };
        });

        setDaySummaries(summaries);
      } catch (error) {
        console.error('Error fetching summaries:', error);
      }
    };

    fetchSummaries();
  }, [selectedProject, periodStartDateStr, viewMode, refreshTrigger]);

  // Navigate date
  const navigateDate = (direction) => {
    if (viewMode === VIEW_MODES.TWO_WEEK) {
      // Move by week in overview
      setSelectedDate(prev => direction === 'prev' ? subDays(prev, 7) : addDays(prev, 7));
    } else {
      // Move by day in daily view
      setSelectedDate(prev => direction === 'prev' ? subDays(prev, 1) : addDays(prev, 1));
    }
  };

  // Switch to day view
  const openDayView = (date) => {
    setSelectedDate(date);
    setViewMode(VIEW_MODES.SINGLE_DAY);
  };

  // Switch back to 2-week view
  const backToOverview = () => {
    setViewMode(VIEW_MODES.TWO_WEEK);
  };

  // Handle refresh
  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
    setDaySummaries({});
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const daysInMonth = getDaysInMonth(calendarMonth);
    const firstDayOfMonth = startOfMonth(calendarMonth);
    const startDayOfWeek = getDay(firstDayOfMonth);
    const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

    const days = [];
    for (let i = 0; i < adjustedStartDay; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  // Generate 3 weeks (21 days) starting from selectedDate
  const days = [];
  for (let i = 0; i < 21; i++) {
    days.push(addDays(selectedDate, i));
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-600"></div>
        <span className="ml-2 text-sm text-slate-600">Loading...</span>
      </div>
    );
  }

  // Get title based on view
  const getTitle = () => {
    if (viewMode === VIEW_MODES.SINGLE_DAY) {
      return 'Local Transport - Daily Planning';
    }
    return 'Local Transport - Overview';
  };

  return (
    <div className="p-3 h-screen bg-steel-50 flex flex-col overflow-hidden">
      {/* Shared Header */}
      <div className="mb-3 bg-white rounded-xl shadow-sm border border-steel-200 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {viewMode === VIEW_MODES.SINGLE_DAY && (
              <button
                onClick={backToOverview}
                className="p-1 text-slate-600 hover:bg-steel-100 rounded mr-1"
                title="Back to Overview"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <Car className="h-5 w-5 text-brand-600" />
            <h1 className="text-base font-black text-slate-900 uppercase tracking-wide">{getTitle()}</h1>
          </div>

          <div className="flex items-center space-x-3">
            {/* Project Selection */}
            <select
              value={selectedProject || ''}
              onChange={(e) => setSelectedProject(e.target.value || null)}
              className="px-3 py-1.5 text-sm border border-steel-300 rounded-xl bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-w-[150px]"
            >
              <option value="">Select project...</option>
              {[...projects]
                .sort((a, b) => {
                  if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
                  return (a.name || '').localeCompare(b.name || '');
                })
                .map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}{!project.is_active ? ' (inactive)' : ''}
                  </option>
                ))
              }
            </select>

            {/* Date Navigation */}
            <div className="flex items-center space-x-1 relative" ref={calendarRef}>
              <button
                onClick={() => navigateDate('prev')}
                className="p-1 text-slate-600 hover:bg-steel-100 rounded"
                title={viewMode === VIEW_MODES.TWO_WEEK ? 'Previous week' : 'Previous day'}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div
                className="text-center min-w-[140px] cursor-pointer hover:bg-steel-100 rounded px-2 py-1"
                onClick={() => {
                  setCalendarMonth(selectedDate);
                  setShowCalendar(!showCalendar);
                }}
              >
                <div className="text-sm font-medium text-slate-900">
                  {format(selectedDate, 'EEE, MMM d, yyyy')}
                </div>
              </div>
              <button
                onClick={() => navigateDate('next')}
                className="p-1 text-slate-600 hover:bg-steel-100 rounded"
                title={viewMode === VIEW_MODES.TWO_WEEK ? 'Next week' : 'Next day'}
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Calendar Dropdown */}
              {showCalendar && (
                <div className="absolute top-full right-0 mt-1 bg-white rounded-xl shadow-xl border border-steel-200 p-3 z-[9999] min-w-[280px]">
                  {/* Month navigation */}
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1))}
                      className="p-1 hover:bg-steel-100 rounded"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm font-medium">
                      {format(calendarMonth, 'MMMM yyyy')}
                    </span>
                    <button
                      onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1))}
                      className="p-1 hover:bg-steel-100 rounded"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => (
                      <div key={d} className="text-center text-xs text-slate-500 py-1">{d}</div>
                    ))}
                  </div>

                  {/* Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {generateCalendarDays().map((day, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          if (day) {
                            setSelectedDate(day);
                            setShowCalendar(false);
                          }
                        }}
                        disabled={!day}
                        className={`p-2 text-sm rounded ${
                          !day ? 'invisible' :
                          isToday(day) ? 'bg-brand-100 text-brand-700 font-bold' :
                          format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd') ? 'bg-brand-500 text-white' :
                          'hover:bg-steel-100'
                        }`}
                      >
                        {day ? format(day, 'd') : ''}
                      </button>
                    ))}
                  </div>

                  {/* Today button */}
                  <button
                    onClick={() => {
                      setSelectedDate(new Date());
                      setShowCalendar(false);
                    }}
                    className="w-full mt-2 px-3 py-1.5 text-sm bg-steel-100 text-slate-700 rounded hover:bg-steel-200"
                  >
                    Today
                  </button>
                </div>
              )}
            </div>

            {/* View toggle button */}
            {viewMode === VIEW_MODES.SINGLE_DAY && (
              <button
                onClick={backToOverview}
                className="px-2 py-1 text-xs bg-steel-100 text-slate-700 rounded hover:bg-steel-200 flex items-center space-x-1"
                title="Show 2-week overview"
              >
                <Grid className="w-3.5 h-3.5" />
                <span>Overview</span>
              </button>
            )}

            {/* Today button */}
            <button
              onClick={() => setSelectedDate(new Date())}
              className="px-3 py-1.5 text-xs font-bold bg-brand-500 text-white rounded-xl hover:bg-brand-600 shadow-lg shadow-brand-500/20"
            >
              Today
            </button>

            <button
              onClick={handleRefresh}
              className="p-1 text-slate-600 hover:bg-steel-100 rounded"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content - conditional based on view mode */}
      {viewMode === VIEW_MODES.SINGLE_DAY ? (
        /* Day View Content */
        <DailyPlanning1Day
          selectedDate={selectedDate}
          selectedProject={selectedProject}
          projects={projects}
          onClose={backToOverview}
          onRefresh={handleRefresh}
        />
      ) : (
        /* Two Week Overview Content */
        <>
          {/* No project selected */}
          {!selectedProject ? (
            <div className="bg-white rounded-xl border border-steel-200 p-6 text-center">
              <Building className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-600">Select a project to view transport planning</p>
            </div>
          ) : (
            <>
              {/* 3-Week Grid (21 days) */}
              <div className="flex-1 flex flex-col gap-2 overflow-hidden">
                {/* Week 1 */}
                <div className="flex-1 grid grid-cols-7 gap-2 min-h-0">
                  {days.slice(0, 7).map((date) => {
                    const dateStr = format(date, 'yyyy-MM-dd');
                    const summary = daySummaries[dateStr] || {};
                    const isCurrentDay = isToday(date);
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                    return (
                      <div
                        key={dateStr}
                        onClick={() => openDayView(date)}
                        className={`flex flex-col rounded-xl border border-steel-200 cursor-pointer transition-all hover:shadow-md hover:border-brand-300 overflow-hidden ${
                          isCurrentDay ? 'ring-2 ring-brand-500 border-brand-500' :
                          isWeekend ? 'bg-steel-50' : 'bg-white'
                        }`}
                      >
                        {/* Card Header */}
                        <div className={`px-2 py-1.5 border-b border-steel-100 ${
                          isCurrentDay ? 'bg-brand-500 text-white' :
                          isWeekend ? 'bg-steel-100 text-slate-600' : 'bg-steel-50 text-slate-700'
                        }`}>
                          <div className="text-xs font-semibold">{format(date, 'EEE')}</div>
                          <div className={`text-[11px] ${isCurrentDay ? 'text-brand-100' : 'text-slate-500'}`}>
                            {format(date, 'd MMM')}
                          </div>
                        </div>

                        {/* Card Content */}
                        <div className="flex-1 p-2 space-y-1 overflow-y-auto">
                          {summary.pendingPickups > 0 && (
                            <div className="flex items-center text-[11px] text-green-700">
                              <Plane className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                              <span><strong>{summary.pendingPickups}</strong> {summary.pendingPickups === 1 ? 'person needs' : 'people need'} pickup</span>
                            </div>
                          )}
                          {summary.pendingDropoffs > 0 && (
                            <div className="flex items-center text-[11px] text-orange-700">
                              <Plane className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 rotate-45" />
                              <span><strong>{summary.pendingDropoffs}</strong> {summary.pendingDropoffs === 1 ? 'person needs' : 'people need'} dropoff</span>
                            </div>
                          )}
                          {summary.pendingToProject > 0 && (
                            <div className="flex items-center text-[11px] text-brand-700">
                              <ArrowRight className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                              <span><strong>{summary.pendingToProject}</strong> {summary.pendingToProject === 1 ? 'person needs' : 'people need'} transport to project</span>
                            </div>
                          )}
                          {summary.pendingFromProject > 0 && (
                            <div className="flex items-center text-[11px] text-indigo-700">
                              <Home className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                              <span><strong>{summary.pendingFromProject}</strong> {summary.pendingFromProject === 1 ? 'person needs' : 'people need'} transport from project</span>
                            </div>
                          )}
                          {summary.scheduledTransports > 0 && (
                            <div className="flex items-center text-[11px] text-slate-600 mt-1 pt-1 border-t border-steel-100">
                              <CheckCircle className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 text-green-500" />
                              <span><strong>{summary.scheduledTransports}</strong> {summary.scheduledTransports === 1 ? 'transport' : 'transports'} scheduled</span>
                            </div>
                          )}
                          {!summary.hasWork && (
                            <div className="flex items-center justify-center h-full text-[11px] text-slate-300">
                              <span>No transports</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Week 2 */}
                <div className="flex-1 grid grid-cols-7 gap-2 min-h-0">
                  {days.slice(7, 14).map((date) => {
                    const dateStr = format(date, 'yyyy-MM-dd');
                    const summary = daySummaries[dateStr] || {};
                    const isCurrentDay = isToday(date);
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                    return (
                      <div
                        key={dateStr}
                        onClick={() => openDayView(date)}
                        className={`flex flex-col rounded-xl border border-steel-200 cursor-pointer transition-all hover:shadow-md hover:border-brand-300 overflow-hidden ${
                          isCurrentDay ? 'ring-2 ring-brand-500 border-brand-500' :
                          isWeekend ? 'bg-steel-50' : 'bg-white'
                        }`}
                      >
                        {/* Card Header */}
                        <div className={`px-2 py-1.5 border-b border-steel-100 ${
                          isCurrentDay ? 'bg-brand-500 text-white' :
                          isWeekend ? 'bg-steel-100 text-slate-600' : 'bg-steel-50 text-slate-700'
                        }`}>
                          <div className="text-xs font-semibold">{format(date, 'EEE')}</div>
                          <div className={`text-[11px] ${isCurrentDay ? 'text-brand-100' : 'text-slate-500'}`}>
                            {format(date, 'd MMM')}
                          </div>
                        </div>

                        {/* Card Content */}
                        <div className="flex-1 p-2 space-y-1 overflow-y-auto">
                          {summary.pendingPickups > 0 && (
                            <div className="flex items-center text-[11px] text-green-700">
                              <Plane className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                              <span><strong>{summary.pendingPickups}</strong> {summary.pendingPickups === 1 ? 'person needs' : 'people need'} pickup</span>
                            </div>
                          )}
                          {summary.pendingDropoffs > 0 && (
                            <div className="flex items-center text-[11px] text-orange-700">
                              <Plane className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 rotate-45" />
                              <span><strong>{summary.pendingDropoffs}</strong> {summary.pendingDropoffs === 1 ? 'person needs' : 'people need'} dropoff</span>
                            </div>
                          )}
                          {summary.pendingToProject > 0 && (
                            <div className="flex items-center text-[11px] text-brand-700">
                              <ArrowRight className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                              <span><strong>{summary.pendingToProject}</strong> {summary.pendingToProject === 1 ? 'person needs' : 'people need'} transport to project</span>
                            </div>
                          )}
                          {summary.pendingFromProject > 0 && (
                            <div className="flex items-center text-[11px] text-indigo-700">
                              <Home className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                              <span><strong>{summary.pendingFromProject}</strong> {summary.pendingFromProject === 1 ? 'person needs' : 'people need'} transport from project</span>
                            </div>
                          )}
                          {summary.scheduledTransports > 0 && (
                            <div className="flex items-center text-[11px] text-slate-600 mt-1 pt-1 border-t border-steel-100">
                              <CheckCircle className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 text-green-500" />
                              <span><strong>{summary.scheduledTransports}</strong> {summary.scheduledTransports === 1 ? 'transport' : 'transports'} scheduled</span>
                            </div>
                          )}
                          {!summary.hasWork && (
                            <div className="flex items-center justify-center h-full text-[11px] text-slate-300">
                              <span>No transports</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Week 3 */}
                <div className="flex-1 grid grid-cols-7 gap-2 min-h-0">
                  {days.slice(14, 21).map((date) => {
                    const dateStr = format(date, 'yyyy-MM-dd');
                    const summary = daySummaries[dateStr] || {};
                    const isCurrentDay = isToday(date);
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                    return (
                      <div
                        key={dateStr}
                        onClick={() => openDayView(date)}
                        className={`flex flex-col rounded-xl border border-steel-200 cursor-pointer transition-all hover:shadow-md hover:border-brand-300 overflow-hidden ${
                          isCurrentDay ? 'ring-2 ring-brand-500 border-brand-500' :
                          isWeekend ? 'bg-steel-50' : 'bg-white'
                        }`}
                      >
                        {/* Card Header */}
                        <div className={`px-2 py-1.5 border-b border-steel-100 ${
                          isCurrentDay ? 'bg-brand-500 text-white' :
                          isWeekend ? 'bg-steel-100 text-slate-600' : 'bg-steel-50 text-slate-700'
                        }`}>
                          <div className="text-xs font-semibold">{format(date, 'EEE')}</div>
                          <div className={`text-[11px] ${isCurrentDay ? 'text-brand-100' : 'text-slate-500'}`}>
                            {format(date, 'd MMM')}
                          </div>
                        </div>

                        {/* Card Content */}
                        <div className="flex-1 p-2 space-y-1 overflow-y-auto">
                          {summary.pendingPickups > 0 && (
                            <div className="flex items-center text-[11px] text-green-700">
                              <Plane className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                              <span><strong>{summary.pendingPickups}</strong> {summary.pendingPickups === 1 ? 'person needs' : 'people need'} pickup</span>
                            </div>
                          )}
                          {summary.pendingDropoffs > 0 && (
                            <div className="flex items-center text-[11px] text-orange-700">
                              <Plane className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 rotate-45" />
                              <span><strong>{summary.pendingDropoffs}</strong> {summary.pendingDropoffs === 1 ? 'person needs' : 'people need'} dropoff</span>
                            </div>
                          )}
                          {summary.pendingToProject > 0 && (
                            <div className="flex items-center text-[11px] text-brand-700">
                              <ArrowRight className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                              <span><strong>{summary.pendingToProject}</strong> {summary.pendingToProject === 1 ? 'person needs' : 'people need'} transport to project</span>
                            </div>
                          )}
                          {summary.pendingFromProject > 0 && (
                            <div className="flex items-center text-[11px] text-indigo-700">
                              <Home className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                              <span><strong>{summary.pendingFromProject}</strong> {summary.pendingFromProject === 1 ? 'person needs' : 'people need'} transport from project</span>
                            </div>
                          )}
                          {summary.scheduledTransports > 0 && (
                            <div className="flex items-center text-[11px] text-slate-600 mt-1 pt-1 border-t border-steel-100">
                              <CheckCircle className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 text-green-500" />
                              <span><strong>{summary.scheduledTransports}</strong> {summary.scheduledTransports === 1 ? 'transport' : 'transports'} scheduled</span>
                            </div>
                          )}
                          {!summary.hasWork && (
                            <div className="flex items-center justify-center h-full text-[11px] text-slate-300">
                              <span>No transports</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Legend */}
              <div className="mt-2 flex items-center justify-center space-x-6 text-[11px] text-slate-500">
                <div className="flex items-center">
                  <Plane className="w-3.5 h-3.5 mr-1 text-green-600" />
                  <span>Airport Pickups</span>
                </div>
                <div className="flex items-center">
                  <Plane className="w-3.5 h-3.5 mr-1 text-orange-600 rotate-45" />
                  <span>Airport Dropoffs</span>
                </div>
                <div className="flex items-center">
                  <ArrowRight className="w-3.5 h-3.5 mr-1 text-brand-600" />
                  <span>To Project</span>
                </div>
                <div className="flex items-center">
                  <Home className="w-3.5 h-3.5 mr-1 text-indigo-600" />
                  <span>From Project</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-3.5 h-3.5 mr-1 text-green-500" />
                  <span>Scheduled</span>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DailyPlanning;
